import type { CSSProperties, RefObject } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Разворот камеры «почти на весь экран» без переноса узла по DOM.
 *
 * ### Почему нельзя просто отрендерить камеру в другом месте
 *
 * Внутри панели живёт `<canvas>` с WebGL-контекстом. Любой перенос узла в
 * другого родителя (портал, `<dialog>`, условный рендер в двух местах)
 * пересоздаёт элемент, а вместе с ним теряется контекст: пришлось бы заново
 * открывать сессию камеры на сервере и качать чанки. Поэтому разворачивается
 * ТОТ ЖЕ САМЫЙ узел — он получает `position: fixed`, а плавность даёт
 * FLIP-анимация (замерили старый прямоугольник → применили конечную раскладку →
 * анимировали обратный transform к нулю).
 *
 * ### Почему стиль инлайновый, а не классом `fixed`
 *
 * Настоящая причина бага «камера не разворачивается, а падает вниз»: панель
 * собрана компонентом `Surface`, у которого в базовых классах стоит `relative`.
 * Классы складываются простым `clsx` (tailwind-merge в проекте нет), поэтому в
 * `class` оказывались ОБА утилити-класса — `relative` и `fixed`. Специфичность у
 * них одинаковая, значит побеждает порядок в таблице стилей, а Tailwind печатает
 * `.fixed` раньше `.relative`. В итоге `position` оставался `relative`, `inset`
 * работал как смещение относительно своего места, а распорка, держащая место в
 * потоке, дополнительно уводила панель вниз — ровно то «падение», которое видел
 * пользователь.
 *
 * Инлайновый стиль выигрывает у любого класса и не зависит от того, какие ещё
 * классы придут снаружи. Отдельно задаётся `z-index`: утилиты `z-*` из шкалы
 * токенов (`z-overlay`) Tailwind v4 не порождает вовсе — в собранной таблице
 * стилей нет ни одного правила `z-index`, — поэтому берём переменную напрямую.
 */

/** Отступ развёрнутой камеры от края вьюпорта: узкий экран / от 640px. */
const INSET = { narrow: 12, wide: 20 }
const WIDE_FROM = 640

/** 360 мс с expo-out: разворот — крупное движение, рывком оно выглядит дёшево. */
const DURATION_MS = 360
const EASING = 'cubic-bezier(0.16, 1, 0.3, 1)'

export interface CameraExpand {
  expanded: boolean
  toggle: () => void
  /** Вешается на разворачиваемый узел. */
  boxRef: RefObject<HTMLDivElement | null>
  /** Высота распорки, держащей место в потоке; `null` — распорка не нужна. */
  placeholder: number | null
  /** Инлайновые стили развёрнутого состояния; `undefined` — обычный поток. */
  style: CSSProperties | undefined
  /** Стиль подложки под развёрнутой камерой. */
  backdropStyle: CSSProperties
}

/**
 * Стили развёрнутой панели. Экспортируются отдельно, чтобы тест мог проверить
 * их без jsdom-раскладки: именно `position` здесь и ломался.
 */
export function expandedStyle(viewportWidth: number): CSSProperties {
  const inset = viewportWidth >= WIDE_FROM ? INSET.wide : INSET.narrow
  return {
    position: 'fixed',
    top: inset,
    right: inset,
    bottom: inset,
    left: inset,
    // Шкала слоёв живёт в токенах; +1 — чтобы панель была над своей подложкой.
    zIndex: 'calc(var(--z-index-overlay) + 1)'
  }
}

export const BACKDROP_STYLE: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 'var(--z-index-overlay)'
}

/**
 * Ближайший предок, который реально скроллится.
 *
 * Единственная скролл-область приложения — не `body`, а `<main>` внутри
 * `AppFrame`. Поэтому `document.body.style.overflow = 'hidden'` под развёрнутой
 * камерой не блокировал ничего: колесо над панелью всплывало в `<main>` и
 * страница под камерой продолжала ехать.
 */
export function scrollableAncestor(node: HTMLElement | null): HTMLElement | null {
  let current = node?.parentElement ?? null
  while (current !== null) {
    const style = getComputedStyle(current)
    const scrolls = style.overflowY === 'auto' || style.overflowY === 'scroll'
    if (scrolls && current.scrollHeight > current.clientHeight) return current
    current = current.parentElement
  }
  return null
}

export function useCameraExpand(): CameraExpand {
  const boxRef = useRef<HTMLDivElement>(null)
  const [expanded, setExpanded] = useState(false)
  const [placeholder, setPlaceholder] = useState<number | null>(null)
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === 'undefined' ? WIDE_FROM : window.innerWidth
  )
  /** Прямоугольник ДО смены раскладки — первый кадр FLIP-анимации. */
  const lastRect = useRef<DOMRect | null>(null)

  const toggle = useCallback(() => {
    const rect = boxRef.current?.getBoundingClientRect() ?? null
    lastRect.current = rect
    setExpanded(previous => {
      // Распорка запоминается ровно в момент разворота: пока камера развёрнута,
      // измерять уже нечего — панель вышла из потока.
      if (!previous && rect !== null) setPlaceholder(rect.height)
      return !previous
    })
  }, [])

  // Ширина вьюпорта нужна только для отступа: сама раскладка на CSS, но
  // `position` и `inset` едут инлайном (см. врезку), значит и брейкпоинт тоже.
  useEffect(() => {
    if (!expanded) return
    const onResize = (): void => setViewportWidth(window.innerWidth)
    onResize()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [expanded])

  useEffect(() => {
    const node = boxRef.current
    const from = lastRect.current
    lastRect.current = null
    if (node === null || from === null) return
    if (typeof node.animate !== 'function') return
    if (globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true) return

    const to = node.getBoundingClientRect()
    if (to.width === 0 || from.width === 0) return

    node.animate(
      [
        {
          transform: `translate(${from.left - to.left}px, ${from.top - to.top}px) scale(${
            from.width / to.width
          }, ${from.height / to.height})`
        },
        { transform: 'none' }
      ],
      { duration: DURATION_MS, easing: EASING }
    )
  }, [expanded])

  // Esc сворачивает; скролл-область под развёрнутой камерой не едет.
  useEffect(() => {
    if (!expanded) return
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      toggle()
    }
    document.addEventListener('keydown', onKey)

    const scroller = scrollableAncestor(boxRef.current)
    const previous = scroller?.style.overflow ?? ''
    if (scroller !== null) scroller.style.overflow = 'hidden'
    const previousBody = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', onKey)
      if (scroller !== null) scroller.style.overflow = previous
      document.body.style.overflow = previousBody
    }
  }, [expanded, toggle])

  return {
    expanded,
    toggle,
    boxRef,
    placeholder: expanded ? placeholder : null,
    style: expanded ? expandedStyle(viewportWidth) : undefined,
    backdropStyle: BACKDROP_STYLE
  }
}
